import { useState, useCallback } from "react";
import { Driver } from "@/types/supabase";
import { createApiService, fetchWithAuth } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { toastHandler } from "@/lib/ToastHandler";

// Define the delivery history response type
interface DeliveryHistoryResponse {
  success: boolean;
  driverId: number;
  branchIds?: number[];
  total: number;
  data: {
    id: number;
    tracking_id: string;
    subtotal: number;
    total_delivery_charge: number;
    discount: number;
    tax: number;
    comment: string;
    is_paid: boolean;
    currency: string;
    order_type: string;
    public_id: string;
    paid_with: string;
    status: string;
    reception_at: string;
    dispatch_date: string;
    created_at: string;
    updated_at: string;
    duration_ms: number;
    is_external: boolean;
    priority: string;
    customer_id: number;
    address_id: number;
    driver_id: number;
    branch_id: number;
    address: {
      id: number;
      city: string;
      street: string;
      latitude: number;
      zip_code: string;
      longitude: number;
      full_address: string;
    };
    branch: {
      id: number;
      name: string;
    };
  }[];
  page: number;
  limit: number;
  totalPages: number;
}

export function useDriversApi() {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages] = useState(1);
  const [currentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken } = useAuth();

  const getTokenWithErrorHandling = useCallback(async () => {
    try {
      const token = await getAccessToken();
      return token;
    } catch (error) {
      setError("Authentication error. Please try logging in again.");
      throw error;
    }
  }, [getAccessToken]);

  const driversService = createApiService<Driver>(
    "/driver/drivers",
    getTokenWithErrorHandling,
    {
      create: "/driver/create-with-invitation",
      update: "/driver/drivers",
      delete: "/driver/drivers",
      getById: "/driver/drivers",
      getAll: "/driver/drivers",
      getMetricsByDriverId: "/driver/drivers/metrics",
    },
  );

  // Fetch driver by ID
  const fetchDriverById = async (driverId: string) => {
    if (!driverId) return;

    setLoading(true);
    try {
      const driver = await driversService.getById(driverId);
      const metrics = await driversService.getMetricsByDriverId(driverId);

      setDrivers((prevDrivers) => {
        const existingIndex = prevDrivers.findIndex((d) => d.id === driver.id);
        if (existingIndex >= 0) {
          const updated = [...prevDrivers];
          updated[existingIndex] = driver;
          return updated;
        } else {
          return [...prevDrivers, driver];
        }
      });
      return { driver, metrics };
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Error al cargar conductor";
      setError(message);
      toastHandler({
        description: message,
        variant: "destructive",
        title: "Error",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Fetch paginated drivers
  const fetchDriversPaginated = async ({
    page = 1,
    limit = 10,
    active,
    vehicle_type,
    brand_id,
    branch_id,
    search,
  }: {
    page?: number;
    limit?: number;
    active?: boolean;
    vehicle_type?: string;
    brand_id?: string | number;
    branch_id?: string | number;
    search?: string;
  }) => {
    setLoading(true);
    try {
      const token = await getTokenWithErrorHandling();

      let url = `/driver/drivers?page=${page}&limit=${limit}`;

      // Add filter parameters to URL
      if (active !== undefined) {
        url += `&active=${active}`;
      }
      if (vehicle_type) {
        url += `&vehicleType=${encodeURIComponent(vehicle_type)}`;
      }
      if (brand_id) {
        url += `&brandId=${brand_id}`;
      }
      if (branch_id) {
        url += `&branchId=${branch_id}`;
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      const response = await fetchWithAuth(url, token);

      if (!response) throw new Error("Error al obtener los conductores");
      // if (response && Array.isArray(response)) {
      //   setDrivers(response);
      //   setTotalCount(response.length || 0);
      // } else {
      //   setDrivers([]);
      //   setTotalCount(0);
      // }
      return response;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Error al cargar conductores";
      setError(message);
      toastHandler({
        description: message,
        variant: "destructive",
        title: "Error",
      });
      setDrivers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverDeliveryHistory = async (
    driverId: number,
    { page = 1, limit = 10, search = "" } = {},
    branchIds?: number[],
  ): Promise<DeliveryHistoryResponse> => {
    try {
      const token = await getTokenWithErrorHandling();
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        ...(search ? { search } : {}),
      });
      if (branchIds && branchIds.length > 0) {
        params.append("branchIds", branchIds.join(","));
      }

      const url = `/order/orders/driver/${driverId}/history?${params.toString()}`;
      return await fetchWithAuth(url, token);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Error al cargar historial de entregas";
      toastHandler({
        description: message,
        variant: "destructive",
        title: "Error",
      });
      return {
        success: false,
        driverId,
        total: 0,
        data: [],
        page: 1,
        limit: 10,
        totalPages: 0,
      };
    }
  };
  // Create a new driver
  const createDriver = async (driverData: any) => {
    try {
      const newDriver = await driversService.create(driverData);
      setDrivers((prev) => [...prev, newDriver]);
      return newDriver;
    } catch (error) {
      setError("Error al crear conductor");
      throw error;
    }
  };

  // Update a driver
  const updateDriver = async (id: number, driverData: Partial<Driver>) => {
    try {
      const updatedDriver = await driversService.update(id, driverData);
      // Replace the old driver with the complete updated driver from the server
      setDrivers((prev) =>
        prev.map((driver) =>
          driver.id === id ? { ...driver, ...updatedDriver } : driver,
        ),
      );
      return updatedDriver;
    } catch (error) {
      console.error("Error updating driver:", error);
      throw error;
    }
  };
  // Delete a driver
  const deleteDriver = async (id: number) => {
    try {
      await driversService.delete(id.toString());
      setDrivers((prev) => prev.filter((driver) => driver.id !== id));
    } catch (error) {
      setError("Error al eliminar conductor");
      throw error;
    }
  };

  // Update a driver's status
  const updateDriverStatus = async (id: number, status: string) => {
    try {
      const token = await getAccessToken();
      const response = await fetchWithAuth(`/drivers/${id}/status`, token, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });

      setDrivers((prev) =>
        prev.map((driver) =>
          driver.id === id ? { ...driver, status } : driver,
        ),
      );

      return response;
    } catch (error) {
      setError("Error al actualizar estado del conductor");
      throw error;
    }
  };

  // Block/Unblock a specific driver
  const blockDriver = async (id: number, block: boolean = true) => {
    try {
      const updatedDriver = await updateDriver(id, { active: !block });

      return updatedDriver;
    } catch (error) {
      console.error(
        `Error al ${block ? "bloquear" : "desbloquear"} driver ${id}:`,
        error,
      );
      throw error;
    }
  };

  return {
    drivers,
    totalCount,
    totalPages,
    currentPage,
    loading,
    error,
    fetchDriversPaginated,
    createDriver,
    updateDriver,
    deleteDriver,
    updateDriverStatus,
    blockDriver,
    fetchDriverDeliveryHistory,
    fetchDriverById,
  };
}
